import React, { useMemo, useState, useEffect } from 'react';
import { Modal, TextInput, Select } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrash,
  faPrint,
  faCheck,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { AreaPrintRule, AreaPrintRulePrinterMapping } from 'shared-models/browser';
import type { ProductView } from '../../../../../services/products/products.service';
import type { CategoryView } from '../../../../../services/categories/categories.service';
import type { PrinterView } from '../../../../../services/printers/printers.service';
import type { AreaView } from '../../../../../services/areas/areas.service';
import { Button } from '../../../../../components/Button';
import navyModal from '../../../../../components/Modal/navy-modal.module.scss';
import classes from './area-print-settings-modal.module.scss';

export type AreaPrintSettingsModalProps = {
  opened: boolean;
  onClose: () => void;
  rules: AreaPrintRule[];
  onSaveRules: (rules: AreaPrintRule[]) => Promise<boolean>;
  isSaving: boolean;
  posProducts: ProductView[];
  categories: CategoryView[];
  activePrinters: PrinterView[];
  areas: AreaView[];
  viewportWidth: number;
};

export const AreaPrintSettingsModal: React.FC<AreaPrintSettingsModalProps> = ({
  opened,
  onClose,
  rules,
  onSaveRules,
  isSaving,
  posProducts,
  categories,
  activePrinters,
  areas,
  viewportWidth,
}) => {
  const [mode, setMode] = useState<'grid' | 'form'>('grid');
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
  const [printerMappings, setPrinterMappings] = useState<AreaPrintRulePrinterMapping[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | 'all'>('all');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) {
      setMode('grid');
      setEditingRuleId(null);
      setDeleteConfirmId(null);
    }
  }, [opened]);

  const activeAreas = useMemo(() => areas.filter((a) => a.isActive), [areas]);

  const activePrinterOptions = useMemo(
    () =>
      activePrinters.map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    [activePrinters],
  );

  const productById = useMemo(
    () => new Map(posProducts.map((p) => [p.id, p])),
    [posProducts],
  );

  const printerById = useMemo(
    () => new Map(activePrinters.map((p) => [p.id, p])),
    [activePrinters],
  );

  const areaById = useMemo(
    () => new Map(activeAreas.map((a) => [a.id, a])),
    [activeAreas],
  );

  const handleOpenCreate = () => {
    if (activePrinters.length < 2) {
      notifications.show({
        color: 'orange',
        title: 'אין מספיק מדפסות',
        message: 'הגדרת הדפסה אזורית דורשת לפחות 2 מדפסות פעילות במערכת.',
        autoClose: 3500,
      });
      return;
    }
    setEditingRuleId(null);
    setNameDraft('');
    setSelectedProductIds(new Set());
    setPrinterMappings([
      { printerId: activePrinters[0].id, areaIds: [] },
      { printerId: activePrinters[1].id, areaIds: [] },
    ]);
    setProductSearch('');
    setSelectedCategoryId('all');
    setMode('form');
  };

  const handleOpenEdit = (rule: AreaPrintRule) => {
    setEditingRuleId(rule.id);
    setNameDraft(rule.name);
    setSelectedProductIds(new Set(rule.productIds));
    setPrinterMappings(rule.printerMappings.map((m) => ({ ...m, areaIds: [...m.areaIds] })));
    setProductSearch('');
    setSelectedCategoryId('all');
    setMode('form');
  };

  const handleDeleteRule = async (ruleId: string) => {
    const nextRules = rules.filter((r) => r.id !== ruleId);
    const success = await onSaveRules(nextRules);
    if (success) {
      setDeleteConfirmId(null);
    }
  };

  const handleToggleProduct = (productId: number) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const filteredProducts = useMemo(() => {
    return posProducts.filter((product) => {
      if (selectedCategoryId !== 'all' && product.categoryId !== selectedCategoryId) {
        return false;
      }
      if (productSearch.trim()) {
        const query = productSearch.trim().toLowerCase();
        return product.name.toLowerCase().includes(query);
      }
      return true;
    });
  }, [posProducts, productSearch, selectedCategoryId]);

  const handleSelectAllFiltered = () => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      for (const p of filteredProducts) {
        next.add(p.id);
      }
      return next;
    });
  };

  const handleClearAllFiltered = () => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      for (const p of filteredProducts) {
        next.delete(p.id);
      }
      return next;
    });
  };

  const handlePrinterChange = (mappingIndex: number, newPrinterIdStr: string | null) => {
    if (!newPrinterIdStr) return;
    const newPrinterId = Number(newPrinterIdStr);
    setPrinterMappings((prev) =>
      prev.map((m, idx) => (idx === mappingIndex ? { ...m, printerId: newPrinterId } : m)),
    );
  };

  const handleToggleAreaOnPrinter = (mappingIndex: number, areaId: number) => {
    setPrinterMappings((prev) =>
      prev.map((m, idx) => {
        if (idx !== mappingIndex) {
          // ensure area is not mapped to other printers in this rule
          return { ...m, areaIds: m.areaIds.filter((id) => id !== areaId) };
        }
        const hasArea = m.areaIds.includes(areaId);
        return {
          ...m,
          areaIds: hasArea ? m.areaIds.filter((id) => id !== areaId) : [...m.areaIds, areaId],
        };
      }),
    );
  };

  const handleAddPrinterSlot = () => {
    const usedPrinters = new Set(printerMappings.map((m) => m.printerId));
    const availablePrinter = activePrinters.find((p) => !usedPrinters.has(p.id)) ?? activePrinters[0];
    if (!availablePrinter) return;
    setPrinterMappings((prev) => [...prev, { printerId: availablePrinter.id, areaIds: [] }]);
  };

  const handleRemovePrinterSlot = (index: number) => {
    if (printerMappings.length <= 2) {
      notifications.show({
        color: 'orange',
        title: 'מינימום מדפסות',
        message: 'חובה להגדיר לפחות 2 מדפסות עבור ניתוב אזורי.',
        autoClose: 2500,
      });
      return;
    }
    setPrinterMappings((prev) => prev.filter((_, idx) => idx !== index));
  };

  const isFormValid =
    nameDraft.trim().length > 0 &&
    selectedProductIds.size > 0 &&
    printerMappings.length >= 2 &&
    new Set(printerMappings.map((m) => m.printerId)).size === printerMappings.length;

  const handleSaveForm = async () => {
    if (!isFormValid || isSaving) return;

    const ruleId = editingRuleId ?? globalThis.crypto?.randomUUID?.() ?? `rule_${Date.now()}`;
    const newRule: AreaPrintRule = {
      id: ruleId,
      name: nameDraft.trim(),
      productIds: Array.from(selectedProductIds),
      printerMappings: printerMappings.map((m) => ({
        printerId: m.printerId,
        areaIds: m.areaIds,
      })),
    };

    const selectedSet = new Set(newRule.productIds);
    const deDuplicatedRules = rules.map((rule) => {
      if (rule.id === newRule.id) {
        return rule;
      }
      const nextProductIds = rule.productIds.filter((productId) => !selectedSet.has(productId));
      return nextProductIds.length === rule.productIds.length
        ? rule
        : { ...rule, productIds: nextProductIds };
    });

    const nextRules = editingRuleId
      ? deDuplicatedRules.map((r) => (r.id === editingRuleId ? newRule : r))
      : [...deDuplicatedRules, newRule];

    const success = await onSaveRules(nextRules);
    if (success) {
      setMode('grid');
      setEditingRuleId(null);
    }
  };

  const titleText =
    mode === 'grid'
      ? 'הגדרות הדפסה אזורית'
      : editingRuleId
      ? 'ערוך הגדרת הדפסה אזורית'
      : 'צור הגדרת הדפסה אזורית';

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (mode === 'form') {
          setMode('grid');
        } else {
          onClose();
        }
      }}
      title={<strong>{titleText}</strong>}
      size={viewportWidth >= 768 ? 'clamp(780px, 80vw, 1150px)' : '100%'}
      centered={viewportWidth >= 768}
      radius="md"
      dir="rtl"
      classNames={{
        root: navyModal.root,
        content: classes.modalContent,
        body: classes.modalViewport,
      }}
      closeOnClickOutside={!isSaving}
    >
      {mode === 'grid' && (
        <div className={classes.gridContainer} dir="rtl">
          <div className={classes.rulesGrid}>
            <div
              className={classes.addRuleCard}
              onClick={handleOpenCreate}
              role="button"
              tabIndex={0}
              data-testid="area-print-add-rule-card"
            >
              <FontAwesomeIcon icon={faPlus as IconProp} className={classes.plusIcon} />
              <span className={classes.addText}>צור הגדרת הדפסה אזורית</span>
            </div>

            {rules.map((rule) => (
              <div key={rule.id} className={classes.ruleCard} data-testid={`area-print-rule-card-${rule.id}`}>
                <div className={classes.ruleCardHeader}>
                  <span className={classes.ruleName}>{rule.name}</span>
                  <div className={classes.cardActions}>
                    <button
                      type="button"
                      className={classes.actionIconBtn}
                      onClick={() => handleOpenEdit(rule)}
                      title="ערוך"
                      aria-label={`ערוך ${rule.name}`}
                      data-testid={`area-print-rule-edit-${rule.id}`}
                    >
                      <FontAwesomeIcon icon={faPen as IconProp} />
                    </button>
                    {deleteConfirmId === rule.id ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Button
                          variant="confirm"
                          size="sm"
                          onClick={() => void handleDeleteRule(rule.id)}
                          loading={isSaving}
                        >
                          אישור
                        </Button>
                        <Button
                          variant="cancel"
                          size="sm"
                          onClick={() => setDeleteConfirmId(null)}
                          disabled={isSaving}
                        >
                          בטל
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`${classes.actionIconBtn} ${classes.deleteBtn}`}
                        onClick={() => setDeleteConfirmId(rule.id)}
                        title="מחק"
                        aria-label={`מחק ${rule.name}`}
                        data-testid={`area-print-rule-delete-${rule.id}`}
                      >
                        <FontAwesomeIcon icon={faTrash as IconProp} />
                      </button>
                    )}
                  </div>
                </div>

                <div className={classes.ruleCardBody}>
                  <div className={classes.productsSummary}>
                    <span className={classes.sectionTitle}>{`מוצרים (${rule.productIds.length}):`}</span>
                    <span className={classes.productsList}>
                      {rule.productIds
                        .map((id) => productById.get(id)?.name)
                        .filter(Boolean)
                        .join(', ') || 'אין מוצרים משויכים'}
                    </span>
                  </div>

                  <div className={classes.printerMappingsSummary}>
                    <span className={classes.sectionTitle}>מדפסות ואזורים משויכים:</span>
                    {rule.printerMappings.map((m, idx) => {
                      const printer = printerById.get(m.printerId);
                      const areaNames = m.areaIds
                        .map((aid) => areaById.get(aid)?.name)
                        .filter(Boolean);
                      return (
                        <div key={idx} className={classes.mappingRow}>
                          <div className={classes.printerHeader}>
                            <FontAwesomeIcon icon={faPrint as IconProp} />
                            <span>{printer?.name ?? `מדפסת #${m.printerId}`}</span>
                          </div>
                          <div className={classes.areasBadgeList}>
                            {areaNames.length > 0 ? (
                              areaNames.map((aname, aidx) => (
                                <span key={aidx} className={classes.areaBadge}>
                                  {aname}
                                </span>
                              ))
                            ) : (
                              <span className={classes.noAreas}>ללא אזורים ספציפיים</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'form' && (
        <div className={classes.formContainer} dir="rtl">
          <div className={classes.formSection}>
            <span className={classes.sectionHeading}>1. שם ההגדרה</span>
            <TextInput
              placeholder="לדוגמה: הדפסת בר ושתייה"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.currentTarget.value)}
              data-testid="area-print-rule-name-input"
              maxLength={80}
            />
          </div>

          <div className={classes.formSection}>
            <div className={classes.sectionHeading}>
              <span>2. בחירת מוצרים ({selectedProductIds.size} נבחרו)</span>
              <div className={classes.quickActionsRow}>
                <button type="button" className={classes.quickActionLink} onClick={handleSelectAllFiltered}>
                  בחר הכל בסינון
                </button>
                <button type="button" className={classes.quickActionLink} onClick={handleClearAllFiltered}>
                  נקה הכל
                </button>
              </div>
            </div>

            <TextInput
              placeholder="חפש מוצר לפי שם..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.currentTarget.value)}
              size="sm"
            />

            <div className={classes.categoryFilterRow}>
              <button
                type="button"
                className={`${classes.categoryChip} ${selectedCategoryId === 'all' ? classes.activeCategory : ''}`}
                onClick={() => setSelectedCategoryId('all')}
              >
                כל המוצרים ({posProducts.length})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`${classes.categoryChip} ${selectedCategoryId === cat.id ? classes.activeCategory : ''}`}
                  onClick={() => setSelectedCategoryId(cat.id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className={classes.productsPickerGrid}>
              {filteredProducts.map((p) => {
                const isSelected = selectedProductIds.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`${classes.productPickCard} ${isSelected ? classes.selectedProduct : ''}`}
                    onClick={() => handleToggleProduct(p.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <span>{p.name}</span>
                    {isSelected && <FontAwesomeIcon icon={faCheck as IconProp} className={classes.productCheck} />}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={classes.formSection}>
            <div className={classes.sectionHeading}>
              <span>3. הגדרת מדפסות ואזורים מנותבים (מינימום 2 מדפסות)</span>
              <button type="button" className={classes.quickActionLink} onClick={handleAddPrinterSlot}>
                + הוסף מדפסת
              </button>
            </div>
            <span className={classes.hintText}>
              יש לבחור לפחות 2 מדפסות. לכל מדפסת סמנו את האזורים שיועברו אליה. אזורים שלא סומנו יודפסו כרגיל.
            </span>

            <div className={classes.printerMappingsGrid}>
              {printerMappings.map((mapping, mIdx) => {
                const otherMappedAreas = new Set(
                  printerMappings
                    .filter((_, idx) => idx !== mIdx)
                    .flatMap((m) => m.areaIds),
                );

                return (
                  <div key={mIdx} className={classes.printerMappingBox}>
                    <div className={classes.printerBoxHeader}>
                      <div className={classes.printerSelectWrap}>
                        <Select
                          data={activePrinterOptions}
                          value={String(mapping.printerId)}
                          onChange={(val) => handlePrinterChange(mIdx, val)}
                          label={`מדפסת ${mIdx + 1}`}
                          size="sm"
                        />
                      </div>
                      {printerMappings.length > 2 && (
                        <button
                          type="button"
                          className={classes.removePrinterBtn}
                          onClick={() => handleRemovePrinterSlot(mIdx)}
                          title="הסר מדפסת זו"
                        >
                          <FontAwesomeIcon icon={faXmark as IconProp} /> הסר מדפסת
                        </button>
                      )}
                    </div>

                    <div className={classes.areaPickChips}>
                      {activeAreas.map((area) => {
                        const isChosenHere = mapping.areaIds.includes(area.id);
                        const isChosenElsewhere = otherMappedAreas.has(area.id);

                        return (
                          <button
                            key={area.id}
                            type="button"
                            disabled={isChosenElsewhere}
                            className={`${classes.areaPickChip} ${isChosenHere ? classes.selectedArea : ''}`}
                            onClick={() => handleToggleAreaOnPrinter(mIdx, area.id)}
                            onMouseUp={(event) => event.currentTarget.blur()}
                            onTouchEnd={(event) => event.currentTarget.blur()}
                          >
                            {area.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={classes.formActions}>
            <Button variant="cancel" onClick={() => setMode('grid')} disabled={isSaving}>
              ביטול
            </Button>
            <Button
              variant="save"
              onClick={() => void handleSaveForm()}
              disabled={!isFormValid || isSaving}
              loading={isSaving}
              dataTestId="area-print-rule-save-btn"
            >
              שמור הגדרה
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
